import { useState } from "react";
import { FolderKanban, Globe, ShieldCheck, Boxes, Archive, Activity, ArrowLeft } from "lucide-react";
import { api } from "@/data/api";
import { useAsync } from "@/hooks/useAsync";
import { healthBadge } from "@/lib/health";
import { AsyncBoundary, EmptyState, Panel, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";

const SERVICE_STATUS = { online: { status: "online", label: "Online" }, offline: { status: "offline", label: "Offline" }, unknown: { status: "unknown", label: "Sem informação" } };
const date = (v) => (v ? new Date(String(v).replace(" ", "T")).toLocaleDateString("pt-BR") : null);

function ProjectView({ id, onBack }) {
  const q = useAsync(() => api.getPortalProject(id), [id]);
  const p = q.data;
  return <div className="space-y-5">
    <Button variant="ghost" size="sm" onClick={onBack}><ArrowLeft className="h-4 w-4" /> Meus Projetos</Button>
    <AsyncBoundary loading={q.loading} error={q.error}>
      {p && <>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="font-heading text-2xl font-bold text-foreground" data-testid="portal-project-name">{p.project.name}</h2>
          <StatusBadge {...healthBadge(p.health)} />
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {p.sites.map((s) => <Panel key={s.primary_domain || s.name} title="Site" icon={Globe} bodyClassName="space-y-2 text-sm">
            <p className="font-mono text-foreground">{s.primary_domain}</p>
            {s.aliases.length > 0 && <p className="text-muted-foreground">Também em: {s.aliases.join(", ")}</p>}
            <StatusBadge {...healthBadge(s.status)} size="sm" pulse={false} />
            <p className="flex items-center gap-2 pt-2"><ShieldCheck className="h-4 w-4 text-ok" />{s.ssl.protected ? <>Protegido por SSL{s.ssl.expires_at ? ` · válido até ${date(s.ssl.expires_at)}` : ""}{s.ssl.renewal ? ` · renovação ${s.ssl.renewal}` : ""}</> : "SSL não configurado"}</p>
          </Panel>)}
          <Panel title="Serviços" icon={Boxes} bodyClassName="space-y-2 text-sm">
            {p.services.length ? p.services.map((s, i) => <div key={i} className="flex items-center justify-between"><span>{s.label}</span><StatusBadge {...SERVICE_STATUS[s.status]} size="sm" pulse={false} /></div>) : <p className="text-muted-foreground">Nenhum serviço.</p>}
          </Panel>
          <Panel title="Backups" icon={Archive} bodyClassName="text-sm">
            {p.backups.last ? <p>Último backup: {date(p.backups.last.at)} · {p.backups.last.status}</p> : <p className="text-muted-foreground">Backups ainda não configurados.</p>}
          </Panel>
          <Panel title="Atividade" icon={Activity} bodyClassName="space-y-1 text-sm">
            {p.activity.length ? p.activity.map((a, i) => <p key={i}><span className="text-muted-foreground">{date(a.at)}</span> · {a.event}</p>) : <p className="text-muted-foreground">Sem atividade recente.</p>}
          </Panel>
        </div>
      </>}
    </AsyncBoundary>
  </div>;
}

// Mesmos Projects/recursos do Admin, na visão permitida pela membership.
export function PortalProjects() {
  const [open, setOpen] = useState(null);
  const q = useAsync(() => api.getPortalProjects(), []);
  if (open) return <ProjectView id={open} onBack={() => setOpen(null)} />;
  return <AsyncBoundary loading={q.loading} error={q.error}>
    {q.data?.length ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="portal-projects">
      {q.data.map((p) => <button key={p.id} onClick={() => setOpen(p.id)} className="rounded-2xl border border-line bg-surface p-5 text-left transition-all hover:-translate-y-1 hover:border-brand/25" data-testid={`portal-project-${p.id}`}>
        <div className="flex items-center justify-between gap-2"><h3 className="font-heading text-lg font-semibold text-foreground">{p.name}</h3><StatusBadge {...healthBadge(p.health)} size="sm" /></div>
        <p className="mt-1 text-sm text-muted-foreground">{p.domains.join(", ") || "Sem site"}</p>
        <p className="mt-3 text-xs text-muted-foreground">{p.services.map((s) => s.label).join(" · ") || "Nenhum serviço"}</p>
      </button>)}
    </div> : <EmptyState icon={FolderKanban} title="Nenhum projeto disponível" description="Seus projetos aparecerão aqui quando o acesso for liberado." />}
  </AsyncBoundary>;
}
